import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getNotifications } from "../../lib/api-notification";
import type {
  NotificationPage,
  NotificationReadAllResult,
  NotificationReadResult,
} from "../../types/notification";

export type NotificationState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; page: NotificationPage };

interface NotificationContextValue {
  state: NotificationState;
  refresh: (showLoading?: boolean) => Promise<void>;
  applyRead: (result: NotificationReadResult) => void;
  applyReadAll: (result: NotificationReadAllResult) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NotificationState>({ status: "loading" });
  const requestRef = useRef<Promise<void> | null>(null);

  const refresh = useCallback((showLoading = false): Promise<void> => {
    if (requestRef.current) return requestRef.current;
    if (showLoading) setState({ status: "loading" });

    const request = getNotifications()
      .then((page) => setState({ status: "loaded", page }))
      .catch(() => {
        if (showLoading) setState({ status: "error" });
      })
      .finally(() => {
        if (requestRef.current === request) requestRef.current = null;
      });
    requestRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    void refresh(true);
    let wasHidden = document.visibilityState !== "visible";
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        wasHidden = true;
      } else if (wasHidden) {
        wasHidden = false;
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refresh]);

  const applyRead = useCallback((result: NotificationReadResult) => {
    setState((current) =>
      current.status === "loaded"
        ? {
            status: "loaded",
            page: {
              ...current.page,
              unread_count: result.unread_count,
              items: current.page.items.map((item) =>
                item.id === result.notification.id ? result.notification : item,
              ),
            },
          }
        : current,
    );
  }, []);

  const applyReadAll = useCallback((result: NotificationReadAllResult) => {
    const readAt = new Date().toISOString();
    setState((current) =>
      current.status === "loaded"
        ? {
            status: "loaded",
            page: {
              ...current.page,
              unread_count: result.unread_count,
              items: current.page.items.map((item) => ({
                ...item,
                read_at: item.read_at ?? readAt,
              })),
            },
          }
        : current,
    );
  }, []);

  return (
    <NotificationContext.Provider value={{ state, refresh, applyRead, applyReadAll }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}
